import React from 'react';

/**
 * Reusable component for formatting dates in a readable format
 * @param {string|Date} date - The date to format (can be a date string, Date object, or period string like "2025-12")
 * @param {string} format - Format type: 'full', 'short', 'month-year', 'period', 'auto'
 * @param {object} options - Additional formatting options
 */
const FormattedDate = ({ date, format = 'auto', className = '', ...options }) => {
  if (!date) return <span className={className}>N/A</span>;

  const formatDate = (dateValue, formatType) => {
    // Handle period strings like "2025-12", "2025-Q1", etc.
    if (typeof dateValue === 'string') {
      // Check if it's a period format (YYYY-MM, YYYY-Q1, etc.)
      if (/^\d{4}-\d{2}$/.test(dateValue)) {
        // YYYY-MM format
        const [year, month] = dateValue.split('-');
        const monthNames = [
          'January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'
        ];
        const monthIndex = parseInt(month, 10) - 1;
        if (formatType === 'month-year' || formatType === 'auto') {
          return `${monthNames[monthIndex]} ${year}`;
        }
        return dateValue;
      } else if (/^\d{4}-Q\d$/.test(dateValue)) {
        // YYYY-Q1 format
        const [year, quarter] = dateValue.split('-Q');
        if (formatType === 'period' || formatType === 'auto') {
          return `Q${quarter} ${year}`;
        }
        return dateValue;
      } else if (/^\d{4}$/.test(dateValue)) {
        // YYYY format
        return dateValue;
      }
    }

    // Try to parse as Date
    let dateObj;
    if (dateValue instanceof Date) {
      dateObj = dateValue;
    } else {
      dateObj = new Date(dateValue);
    }

    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return dateValue; // Return original if invalid
    }

    // Format based on type
    switch (formatType) {
      case 'full':
        return dateObj.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          ...options
        });
      
      case 'short':
        return dateObj.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          ...options
        });
      
      case 'month-year':
        return dateObj.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          ...options
        });
      
      case 'date-only':
        return dateObj.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          ...options
        });
      
      case 'auto':
      default:
        // Auto-detect best format
        const now = new Date();
        const diffTime = Math.abs(now - dateObj);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays < 7) {
          // Recent dates: show day and date
          return dateObj.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            ...options
          });
        } else if (diffDays < 365) {
          // This year: show month and day
          return dateObj.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            ...options
          });
        } else {
          // Older dates: show month and year
          return dateObj.toLocaleDateString('en-US', {
            month: 'short',
            year: 'numeric',
            ...options
          });
        }
    }
  };

  const formattedDate = formatDate(date, format);

  return <span className={className}>{formattedDate}</span>;
};

export default FormattedDate;

